import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import type { ChipHero, ChipProgress, Tone } from '../utils/bonus-indicators';
import { HeroPodium } from './bonus-chip-podium';

const HERO_TONE: Record<Tone, string> = {
  success: 'text-success',
  warning: 'text-text-primary',
  muted: 'text-text-primary',
};

const FILL_TONE: Record<Tone, string> = {
  success: 'bg-success',
  warning: 'bg-error',
  muted: 'bg-text-tertiary/40',
};

function HeroAmount({ value, tone }: { value: string; tone: Tone }): ReactNode {
  return (
    <p
      className={cn(
        'font-mono text-3xl font-bold leading-none tabular-nums',
        HERO_TONE[tone]
      )}
    >
      {value}
    </p>
  );
}

function HeroDots({
  done,
  total,
  tone,
}: {
  done: number;
  total: number;
  tone: Tone;
}): ReactNode {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={cn(
            'h-3 w-3 rounded-full',
            i < done ? FILL_TONE[tone] : 'border border-text-tertiary/30'
          )}
        />
      ))}
    </div>
  );
}

export function HeroBlock({
  hero,
  tone,
}: {
  hero: ChipHero;
  tone: Tone;
}): ReactNode {
  if (hero.kind === 'amount') {
    return <HeroAmount value={hero.value} tone={tone} />;
  }
  if (hero.kind === 'dots') {
    return <HeroDots done={hero.done} total={hero.total} tone={tone} />;
  }
  return <HeroPodium rows={hero.rows} userOffPodium={hero.userOffPodium} />;
}

export function ChipProgressBar({
  progress,
  tone,
}: {
  progress: ChipProgress;
  tone: Tone;
}): ReactNode {
  const pct = Math.min(
    100,
    Math.round((progress.current / progress.goal) * 100)
  );
  return (
    <div className="space-y-1">
      <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
        <div
          className={cn('h-full rounded-full transition-all', FILL_TONE[tone])}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="font-mono text-[10px] tabular-nums text-text-tertiary/70">
        {progress.current} / {progress.goal}
      </p>
    </div>
  );
}
